import { Head, router, usePage } from '@inertiajs/react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  locale: string
  rtl: boolean
  event: {
    name: string
    primaryColor: string
    secondaryColor: string
    font: string | null
    customFontUrl: string | null
    customFontName: string | null
    bannerUrl: string | null
  }
  eventUser: {
    firstName: string | null
    lastName: string | null
    rsvp: string | null
  }
  flash: Record<string, string | undefined>
  urls: {
    confirm: string
    decline: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Rsvp({ locale, rtl, event, eventUser, flash, urls }: Props) {
  const dir = rtl ? 'rtl' : 'ltr'
  const userName = [eventUser.firstName, eventUser.lastName].filter(Boolean).join(' ')

  /* Custom font injection */
  const fontFamily = event.customFontUrl
    ? event.customFontName || 'EventFont'
    : event.font || 'sans-serif'

  function handleConfirm() {
    router.post(urls.confirm)
  }

  function handleDecline() {
    router.post(urls.decline)
  }

  const alreadyResponded = eventUser.rsvp === 'confirmed' || eventUser.rsvp === 'declined'

  return (
    <>
      <Head title={`RSVP — ${event.name}`} />

      {event.customFontUrl && (
        <style>{`
          @font-face {
            font-family: '${fontFamily}';
            src: url('${event.customFontUrl}') format('truetype');
            font-weight: normal;
            font-style: normal;
          }
        `}</style>
      )}

      <div
        dir={dir}
        className="flex min-h-screen flex-col items-center bg-gray-50"
        style={{ fontFamily }}
      >
        {/* Banner */}
        {event.bannerUrl && (
          <div className="w-full max-w-2xl overflow-hidden rounded-b-2xl shadow-sm">
            <img
              src={event.bannerUrl}
              alt={event.name}
              className="h-48 w-full object-cover sm:h-64"
            />
          </div>
        )}

        {/* Card */}
        <div className="mx-auto w-full max-w-md px-4 py-10">
          <div className="rounded-2xl bg-white p-8 shadow-lg text-center">
            {/* Event name */}
            <h1
              className="text-2xl font-bold mb-2"
              style={{ color: event.primaryColor }}
            >
              {event.name}
            </h1>

            {/* Greeting */}
            <p className="text-gray-600 mb-8">
              {locale === 'ar' ? (
                <>مرحباً <span className="font-semibold" style={{ color: event.primaryColor }}>{userName}</span></>
              ) : (
                <>Hello <span className="font-semibold" style={{ color: event.primaryColor }}>{userName}</span></>
              )}
            </p>

            {/* Flash messages */}
            {(flash.notice || flash.alert) && (
              <div
                className={`mb-6 rounded-lg px-4 py-3 text-sm ${
                  flash.alert
                    ? 'bg-red-50 text-red-700'
                    : 'bg-green-50 text-green-700'
                }`}
              >
                {flash.notice || flash.alert}
              </div>
            )}

            {alreadyResponded ? (
              <div
                className={`rounded-lg px-6 py-4 text-sm font-medium ${
                  eventUser.rsvp === 'confirmed'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {eventUser.rsvp === 'confirmed'
                  ? locale === 'ar'
                    ? 'لقد تم تأكيد حضورك ✓'
                    : 'Your attendance has been confirmed ✓'
                  : locale === 'ar'
                    ? 'لقد تم رفض الدعوة'
                    : 'You have declined the invitation'}
              </div>
            ) : (
              <>
                <p className="text-gray-500 text-sm mb-8">
                  {locale === 'ar'
                    ? 'هل ترغب في تأكيد حضورك؟'
                    : 'Would you like to confirm your attendance?'}
                </p>

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <button
                    onClick={handleConfirm}
                    className="rounded-lg px-8 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: event.primaryColor }}
                  >
                    {locale === 'ar' ? 'تأكيد الحضور' : 'Confirm Attendance'}
                  </button>
                  <button
                    onClick={handleDecline}
                    className="rounded-lg border px-8 py-3 text-sm font-semibold transition-colors hover:bg-gray-50"
                    style={{ borderColor: event.secondaryColor, color: event.secondaryColor }}
                  >
                    {locale === 'ar' ? 'الاعتذار' : 'Decline'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
